import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles, Tooltip } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { useSkillForm } from './SkillFormContext'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    flexShrink: 0
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px'
  },
  field: {
    marginBottom: '14px',
    '&:last-child': { marginBottom: 0 }
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  charCount: {
    fontWeight: 400,
    fontSize: '10px',
    color: 'var(--color-neutral-400)'
  },
  description: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    marginBottom: '4px',
    lineHeight: 1.4
  },
  textarea: {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  listWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  listIndex: {
    width: '18px',
    textAlign: 'right',
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    flexShrink: 0,
    userSelect: 'none'
  },
  listInput: {
    flex: 1,
    padding: '5px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'none',
    overflow: 'hidden',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  listActions: {
    display: 'flex',
    gap: '1px',
    flexShrink: 0
  },
  rowBtn: {
    padding: 2,
    color: 'var(--color-neutral-300)',
    '&:hover': {
      color: 'var(--color-neutral-600)',
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  deleteBtn: {
    padding: 2,
    color: 'var(--color-neutral-300)',
    '&:hover': {
      color: 'var(--color-danger-600, #dc2626)',
      backgroundColor: 'var(--color-danger-050, #fef2f2)'
    }
  },
  addBtn: {
    padding: 2,
    color: 'var(--color-neutral-400)',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  }
}))

function ListInput ({ items, onChange, placeholder, minItems }) {
  const classes = useStyles()
  const min = minItems || 0

  useEffect(() => {
    if (items.length < min) {
      const pad = Array.from({ length: min - items.length }, () => '')
      onChange([...items, ...pad])
    }
  }, [items, min, onChange])

  const addItem = useCallback(() => {
    onChange([...items, ''])
  }, [items, onChange])

  const updateItem = useCallback((index, value) => {
    const next = [...items]
    next[index] = value
    onChange(next)
  }, [items, onChange])

  const removeItem = useCallback((index) => {
    if (items.length <= min) return
    onChange(items.filter((_, i) => i !== index))
  }, [items, onChange, min])

  const moveItem = useCallback((index, direction) => {
    const target = index + direction
    if (target < 0 || target >= items.length) return
    const next = [...items]
    const temp = next[index]
    next[index] = next[target]
    next[target] = temp
    onChange(next)
  }, [items, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (items[index]?.trim()) {
        onChange([...items, ''])
        setTimeout(() => {
          const inputs = e.target.closest(`.${classes.listWrap}`)?.querySelectorAll('input')
          inputs?.[inputs.length - 1]?.focus()
        }, 0)
      }
    } else if (e.key === 'Backspace' && items[index] === '' && items.length > min) {
      e.preventDefault()
      removeItem(index)
    }
  }, [items, onChange, removeItem, min, classes.listWrap])

  return (
    <div className={classes.listWrap}>
      {items.map((item, i) => (
        <div key={i} className={classes.listRow}>
          <span className={classes.listIndex}>{i + 1}.</span>
          <textarea
            className={classes.listInput}
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            placeholder={placeholder}
            rows={1}
            autoFocus={item === '' && i === items.length - 1 && i >= min}
          />
          <div className={classes.listActions}>
            <Tooltip title='Move up' arrow>
              <span>
                <IconButton
                  size='small'
                  className={classes.rowBtn}
                  onClick={() => moveItem(i, -1)}
                  disabled={i === 0}
                >
                  <ArrowUpwardIcon style={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Move down' arrow>
              <span>
                <IconButton
                  size='small'
                  className={classes.rowBtn}
                  onClick={() => moveItem(i, 1)}
                  disabled={i === items.length - 1}
                >
                  <ArrowDownwardIcon style={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Remove' arrow>
              <span>
                <IconButton
                  size='small'
                  className={classes.deleteBtn}
                  onClick={() => removeItem(i)}
                  disabled={items.length <= min}
                >
                  <CloseIcon style={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      ))}
      <div>
        <Tooltip title='Add item' arrow>
          <IconButton size='small' className={classes.addBtn} onClick={addItem}>
            <AddIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  )
}

ListInput.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minItems: PropTypes.number
}

function SettingsPanel () {
  const classes = useStyles()
  const form = useSkillForm()

  return (
    <div className={classes.root}>
      <div className={classes.header}>Settings</div>
      <div className={classes.body}>
        <div className={classes.field}>
          <div className={classes.label}>
            <span>Description</span>
            <span className={classes.charCount}>{form.description.length}/200</span>
          </div>
          <div className={classes.description}>Describe this skill to users and to Denali AI</div>
          <textarea
            className={classes.textarea}
            value={form.description}
            onChange={(e) => form.setDescription(e.target.value)}
            rows={3}
            maxLength={200}
          />
        </div>

        <div className={classes.field}>
          <div className={classes.label}>
            <span>Selection Triggers</span>
            <span className={classes.charCount}>{form.useWhen.filter(Boolean).length} items</span>
          </div>
          <div className={classes.description}>Describe situations where this skill should be chosen. The model uses these to decide when to activate it.</div>
          <ListInput
            items={form.useWhen}
            onChange={form.setUseWhen}
            placeholder='e.g. User asks about portfolio performance...'
            minItems={1}
          />
        </div>

        <div className={classes.field}>
          <div className={classes.label}>
            <span>Exclusions</span>
            <span className={classes.charCount}>{form.dontUseWhen.filter(Boolean).length} items</span>
          </div>
          <div className={classes.description}>Counter-examples &mdash; situations where this skill should NOT be chosen, even if triggers match.</div>
          <ListInput
            items={form.dontUseWhen}
            onChange={form.setDontUseWhen}
            placeholder='e.g. User is asking about a different topic...'
          />
        </div>
      </div>
    </div>
  )
}

export default SettingsPanel
